import type { CustomNode, ConditionStatement } from '@src/components/custom-nodes/types';
import { v4 as uuid } from 'uuid';
import { NODES_GAP, NODE_HEIGHT } from '@src/consts';
import { updateNode, deleteNode } from '@src/scenario-editor/utils';
import type { ReactFlowInstance } from 'reactflow';

interface GetIfBranchNodeProps {
  reactFlowInstance?: ReactFlowInstance;
  position: { x: number; y: number };
  title?: string;
  branchIndex: number;
  parentNodeId: string;
  parentBranchId: string;
}
export const getIfBranchNode = ({
  position,
  title = '',
  branchIndex,
  parentNodeId,
  parentBranchId,
}: GetIfBranchNodeProps) => {
  const newIfBranchNode = {
    id: uuid(),
    type: 'IfBranchNode',
    selectable: false,
    position: {
      ...position,
      y: position.y + NODE_HEIGHT + NODES_GAP,
    },
    data: {
      label: title + `-${branchIndex}`,
      handles: {
        t: true,
        b: true
      },
      message: `Option ${branchIndex}`,
      nodeType: 'if:branch',
      dependsOn: parentNodeId
    },
    branchId: parentBranchId + `-${branchIndex}`,
    parentBranchId,
    parentNodeId,
    style: {
      pointerEvents: 'none',
    },
  };

  return newIfBranchNode as CustomNode;
}

interface GetIfConditionNodeProps {
  reactFlowInstance?: ReactFlowInstance;
  position: { x: number; y: number };
  title?: string;
  parentNodeId: string;
  parentBranchId: string;
  condStatements?: ConditionStatement[];
  branchId?: string;
}
export const getIfConditionNode = ({
  position,
  title = '',
  parentNodeId,
  parentBranchId,
  condStatements,
  branchId,
}: GetIfConditionNodeProps) => {
  const newIfConditionNode = {
    id: uuid(),
    type: 'IfConditionNode',
    selected: true,
    position,
    data: {
      title,
      handles: {
        t: true,
        b: true
      },
      message: '',
      nodeType: 'if:condition',
      condStatements,
    },
    branchId,
    parentBranchId,
    parentNodeId,
  };

  return newIfConditionNode as CustomNode;
}

export const getIfConditionCloseNode = ({
  reactFlowInstance,
  position,
  title = '',
  parentNodeId,
  parentBranchId,
  branchId,
}: Exclude<GetIfConditionNodeProps, 'condStatements'>) => {
  const updateItem = updateNode({ reactFlowInstance });
  const deleteItem = deleteNode({ reactFlowInstance });

  const newNodeClose = {
    id: uuid(),
    type: 'IfCloseNode',
    selectable: false,
    position: {
      ...position,
      y: position.y + 2*NODE_HEIGHT + 2*NODES_GAP,
    },
    data: {
      label: title + '-close',
      handles: {
        l: true,
        r: true,
        t: true,
        b: true,
      },
      message: 'If condition close',
      nodeType: 'if:condition-close',
      updateItem,
      deleteItem,
      dependsOn: parentNodeId,
    },
    branchId,
    parentBranchId,
    parentNodeId,
    style: {
      pointerEvents: 'none',
    },
  };

  return newNodeClose as CustomNode;
};

export const getIfConditionBlock = ({
  reactFlowInstance,
  position,
  title,
  parentNodeId,
  parentBranchId,
  condStatements,
  branchId,
}: GetIfConditionNodeProps) => {
  const ifConditionNode = getIfConditionNode({
    reactFlowInstance,
    position,
    title,
    parentNodeId,
    parentBranchId,
    condStatements,
    branchId,
  });
  const ifConditionNodeClose = getIfConditionCloseNode({
    reactFlowInstance,
    position,
    title,
    parentNodeId: ifConditionNode.id,
    parentBranchId,
    branchId,
  });

  return {
    ifConditionNode,
    ifBranchNodes: condStatements!.map((_, index) => getIfBranchNode({
      reactFlowInstance,
      position,
      title,
      branchIndex: index + 1,
      parentNodeId: ifConditionNode.id,
      parentBranchId: ifConditionNode.branchId,
    })),
    ifConditionNodeClose,
  };
};
